import React, { useState } from 'react';

const ReviewsBlock = ({ data, isSelected, onSelect, onUpdate, isPreview }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data);

  const handleSave = () => {
    onUpdate(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addReview = () => {
    setEditData(prev => ({
      ...prev,
      reviews: [...(prev.reviews || []), {
        id: Date.now(),
        name: 'New Customer',
        rating: 5,
        comment: 'Great product!',
        date: new Date().toISOString().split('T')[0]
      }]
    }));
  };

  const updateReview = (index, field, value) => {
    setEditData(prev => ({
      ...prev,
      reviews: prev.reviews.map((review, i) => 
        i === index ? { ...review, [field]: value } : review
      )
    }));
  };

  const removeReview = (index) => {
    setEditData(prev => ({
      ...prev,
      reviews: prev.reviews.filter((_, i) => i !== index)
    }));
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} style={{ color: i < rating ? '#ffc107' : '#e9ecef' }}>
        ★
      </span>
    ));
  };

  if (isPreview) {
    const reviewsToShow = editData.reviews?.slice(0, editData.maxReviews || 6) || [];
    
    return (
      <section style={{
        padding: '4rem 0',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#333'
            }}>
              {data.title || 'What Our Customers Say'}
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: '#666',
              lineHeight: '1.6'
            }}>
              {data.description || 'Read reviews from our satisfied customers'}
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {reviewsToShow.map((review, index) => (
              <div
                key={review.id || index}
                style={{
                  backgroundColor: 'white',
                  padding: '2rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  position: 'relative'
                }}
              >
                {data.showRatings && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    {renderStars(review.rating)}
                    <span style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#666'
                    }}>
                      {review.rating}/5
                    </span>
                  </div>
                )}
                
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: '#333',
                  marginBottom: '1.5rem',
                  fontStyle: 'italic'
                }}>
                  "{review.comment}"
                </p>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: '#666'
                  }}>
                    {review.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '0.25rem'
                    }}>
                      {review.name}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#666'
                    }}>
                      {new Date(review.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {reviewsToShow.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⭐</div>
              <p>No reviews yet. Be the first to leave a review!</p>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <div
      style={{
        border: isSelected ? '2px solid #007bff' : '2px solid #dee2e6',
        borderRadius: '0.5rem',
        backgroundColor: 'white',
        marginBottom: '1rem',
        position: 'relative'
      }}
      onClick={onSelect}
    >
      {/* Block Header */}
      <div style={{
        padding: '0.75rem 1rem',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>⭐</span>
          <span style={{ fontWeight: '600' }}>Reviews Section</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(!isEditing);
          }}
          style={{
            padding: '0.25rem 0.5rem',
            backgroundColor: isEditing ? '#dc3545' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      {/* Block Content */}
      <div style={{ padding: '1rem' }}>
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Title */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Section Title
              </label>
              <input
                type="text"
                value={editData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="2"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Max Reviews */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Maximum Reviews to Show
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={editData.maxReviews || 6}
                onChange={(e) => handleInputChange('maxReviews', parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Show Ratings */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={editData.showRatings || false}
                onChange={(e) => handleInputChange('showRatings', e.target.checked)}
                style={{ transform: 'scale(1.2)' }}
              />
              <label style={{ fontWeight: '500' }}>Show Star Ratings</label>
            </div>

            {/* Reviews Management */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: '500' }}>Reviews ({editData.reviews?.length || 0})</label>
                <button
                  onClick={addReview}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  + Add Review
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto' }}>
                {editData.reviews?.map((review, index) => (
                  <div key={review.id || index} style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '0.25rem',
                    border: '1px solid #dee2e6'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        placeholder="Customer Name"
                        value={review.name}
                        onChange={(e) => updateReview(index, 'name', e.target.value)}
                        style={{
                          padding: '0.25rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem'
                        }}
                      />
                      <input
                        type="number"
                        min="1"
                        max="5"
                        placeholder="Rating"
                        value={review.rating}
                        onChange={(e) => updateReview(index, 'rating', parseInt(e.target.value))}
                        style={{
                          padding: '0.25rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <textarea
                      placeholder="Review comment"
                      value={review.comment}
                      onChange={(e) => updateReview(index, 'comment', e.target.value)}
                      rows="2"
                      style={{
                        width: '100%',
                        padding: '0.25rem',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.25rem',
                        fontSize: '0.875rem',
                        resize: 'vertical',
                        marginBottom: '0.5rem'
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <input
                        type="date"
                        value={review.date}
                        onChange={(e) => updateReview(index, 'date', e.target.value)}
                        style={{
                          padding: '0.25rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem'
                        }}
                      />
                      <button
                        onClick={() => removeReview(index)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )) || []}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.25rem',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
              {data.title || 'Reviews Section'}
            </h3>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>
              {data.reviews?.length || 0} review(s) configured
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsBlock;
